import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import config from '../config';
import organizationService from '../services/organizationService';
import Logger from '../utils/logger';
import JwtUtils from '../utils/jwtUtils';
import authStorage from '../utils/authStorage';

export const OrganizationContext = createContext(null);

export const OrganizationProvider = ({ children }) => {
  const [organizations, setOrganizations] = useState([]);
  const [activeOrganization, setActiveOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // La notion "organization" reste interne (backend / scoping), mais l'UI ne doit pas l'exposer en mode entreprise.
  const ACTIVE_ORG_KEY = 'Nixia_active_organization_id';
  const forceSingleOrganization = Boolean(config?.enterprise?.forceSingleOrganization);

  const selectOrganization = useCallback((org) => {
    if (!org) return;
    setActiveOrganization(org);
    localStorage.setItem(ACTIVE_ORG_KEY, org.id);
  }, []);

  const loadOrganizations = useCallback(async (forceReload = false) => {
    try {
      const token = authStorage.getItem('token');
      if (!token) {
        setOrganizations([]);
        setActiveOrganization(null);
        setError(null);
        setIsInitialized(true);
        setLoading(false);
        return;
      }

      // FIX: Don't show loading spinner if already initialized and not forcing reload
      if (!isInitialized || forceReload) {
        setLoading(true);
      }
      setError(null);

      // Avoid protected calls until we have a valid session.
      // Organizations are loaded on authStateChanged after login.
      if (!JwtUtils.validateAndCleanToken()) {
        setOrganizations([]);
        setActiveOrganization(null);
        localStorage.removeItem(ACTIVE_ORG_KEY);
        setIsInitialized(true);
        return;
      }

      const fetchOrgs = async () => {
        try {
          return await organizationService.getOrganizations();
        } catch (err) {
          await new Promise(resolve => setTimeout(resolve, 500));
          return await organizationService.getOrganizations();
        }
      };

      const data = await fetchOrgs();
      const orgList = data.organizations || [];
      setOrganizations(orgList);

      if (!orgList.length) {
        setActiveOrganization(null);
        localStorage.removeItem(ACTIVE_ORG_KEY);
        setIsInitialized(true);
        return;
      }

      const defaultOrg = orgList.find(org => (org.is_default_for_user ?? org.is_default)) || orgList[0];

      if (forceSingleOrganization) {
        selectOrganization(defaultOrg);
      } else {
        const savedOrgId = localStorage.getItem(ACTIVE_ORG_KEY);
        const savedOrg = savedOrgId ? orgList.find(org => org.id === savedOrgId) : null;
        selectOrganization(savedOrg || defaultOrg);
      }

      setIsInitialized(true);
    } catch (err) {
      Logger.error('Error loading organizations:', err);
      setError('Erreur lors du chargement des données');
      setOrganizations(prev => prev || []);
      setActiveOrganization(prev => prev || null);
    } finally {
      setLoading(false);
    }
  }, [forceSingleOrganization, isInitialized, selectOrganization]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    const handleAuthChange = () => {
      setIsInitialized(false);
      loadOrganizations(true);
    };

    window.addEventListener('authStateChanged', handleAuthChange);
    return () => window.removeEventListener('authStateChanged', handleAuthChange);
  }, [loadOrganizations]);

  const switchOrganization = useCallback((organizationId) => {
    if (forceSingleOrganization && organizationId === null) return;

    if (organizationId === null) {
      setSwitching(true);
      setTimeout(() => {
        setActiveOrganization(null);
        localStorage.removeItem(ACTIVE_ORG_KEY);
        localStorage.removeItem('Nixia_active_agent_id');
        localStorage.removeItem('nixia_active_agent_id');
        localStorage.removeItem('Nixia_active_agent_org_id');
        window.dispatchEvent(new CustomEvent('organizationChanged', {
          detail: { organizationId: null, organization: null },
        }));
        setSwitching(false);
      }, 300);
      return;
    }

    const org = organizations.find(o => o.id === organizationId);
    if (!org) {
      Logger.warning('Organization not found:', organizationId);
      return;
    }

    setSwitching(true);
    setTimeout(() => {
      setActiveOrganization(org);
      localStorage.setItem(ACTIVE_ORG_KEY, organizationId);
      localStorage.removeItem('Nixia_active_agent_id');
      localStorage.removeItem('nixia_active_agent_id');
      localStorage.removeItem('Nixia_active_agent_org_id');
      window.dispatchEvent(new CustomEvent('organizationChanged', {
        detail: { organizationId, organization: org },
      }));
      setSwitching(false);
    }, 300);
  }, [forceSingleOrganization, organizations]);

  const refreshOrganizations = useCallback(async () => {
    await loadOrganizations(true);
  }, [loadOrganizations]);

  const getActiveOrganizationId = useCallback(() => activeOrganization?.id || null, [activeOrganization]);
  const isOrganizationActive = useCallback((organizationId) => activeOrganization?.id === organizationId, [activeOrganization]);

  const activeOrganizationRole = activeOrganization?.member_role || null;
  const activeOrganizationIsShared = Boolean(activeOrganization?.is_shared);
  const activeOrganizationIsAdminMember = activeOrganization?.is_admin_member !== undefined && activeOrganization?.is_admin_member !== null
    ? Boolean(activeOrganization.is_admin_member)
    : (activeOrganizationRole ? ['ADMIN', 'ADMIN_PRINCIPAL'].includes(activeOrganizationRole) : true);

  const value = {
    organizations,
    activeOrganization,
    activeOrganizationRole,
    activeOrganizationIsShared,
    activeOrganizationIsAdminMember,
    loading,
    error,
    switching,
    isInitialized,
    switchOrganization,
    refreshOrganizations,
    getActiveOrganizationId,
    isOrganizationActive,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) throw new Error('useOrganization must be used within an OrganizationProvider');
  return context;
};

